import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import type { PostDto, PostListDto } from '@plugins/newsfeed-lib/types';

@Injectable({
  providedIn: 'root',
})
export class PostsService {
  private readonly http = inject(HttpClient);

  /** Published feed. The server defines the order, never re-sort it. */
  getFeed(
    skip: number,
    take: number,
    channel: string | null = null,
  ): Observable<PostListDto> {
    return this.http.get<PostListDto>('/api/posts', {
      // `All` omits the param entirely, so the unfiltered request stays
      // byte-identical to what it was before the filter existed.
      params: { skip, take, ...(channel ? { channel } : {}) },
    });
  }

  /**
   * Top news for the carousel. The server bounds the window and the count, so
   * there is nothing to paginate — only the channel filter travels.
   */
  getTopPosts(channel: string | null = null): Observable<PostListDto> {
    return this.http.get<PostListDto>('/api/posts/top', {
      params: channel ? { channel } : {},
    });
  }

  getPost(id: string): Observable<PostDto> {
    return this.http.get<PostDto>(`/api/posts/${id}`);
  }

  /** Idempotent, returns the post with the updated read state. */
  markRead(id: string): Observable<PostDto> {
    return this.http.post<PostDto>(`/api/posts/${id}/read`, {});
  }

  /** Idempotent, only allowed for posts that require acknowledgement. */
  acknowledge(id: string): Observable<PostDto> {
    return this.http.post<PostDto>(`/api/posts/${id}/acknowledge`, {});
  }

  /**
   * Idempotent desired-state calls, never a toggle: a retried request must not
   * silently invert the like.
   */
  like(id: string): Observable<PostDto> {
    return this.http.post<PostDto>(`/api/posts/${id}/like`, {});
  }

  unlike(id: string): Observable<PostDto> {
    return this.http.delete<PostDto>(`/api/posts/${id}/like`);
  }
}
